import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import './vaccination-certificate.css'

const CERTIFICATE_FEE = 2000

const emptyForm = {
  fullName: '',
  cnic: '',
  passportNumber: '',
  email: '',
  phone: '',
}

function VaccinationCertificate() {
  const { vaccine } = useParams()
  const navigate = useNavigate()
  const vaccineName = decodeURIComponent(vaccine || '')

  const [step, setStep] = useState('details') // 'details' | 'payment' | 'done'
  const [form, setForm] = useState(emptyForm)
  const [photo, setPhoto] = useState(null) // { file, previewUrl }
  const [paymentScreenshot, setPaymentScreenshot] = useState(null) // { file, previewUrl }
  const [submitting, setSubmitting] = useState(false)
  const [applicationId, setApplicationId] = useState(null)

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (photo) URL.revokeObjectURL(photo.previewUrl)
    setPhoto({ file, previewUrl: URL.createObjectURL(file) })
  }

  const removePhoto = () => {
    if (photo) URL.revokeObjectURL(photo.previewUrl)
    setPhoto(null)
  }

  const handlePaymentScreenshotChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (paymentScreenshot) URL.revokeObjectURL(paymentScreenshot.previewUrl)
    setPaymentScreenshot({ file, previewUrl: URL.createObjectURL(file) })
  }

  const removePaymentScreenshot = () => {
    if (paymentScreenshot) URL.revokeObjectURL(paymentScreenshot.previewUrl)
    setPaymentScreenshot(null)
  }

  const handleDetailsSubmit = (e) => {
    e.preventDefault()
    if (!form.fullName.trim() || !form.cnic.trim() || !form.passportNumber.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields.')
      return
    }
    if (!photo) {
      toast.error('Please upload a passport-size photo.')
      return
    }
    setStep('payment')
  }

  const handlePaymentConfirm = async () => {
    if (!paymentScreenshot) {
      toast.error('Please upload a screenshot of your payment transfer.')
      return
    }

    setSubmitting(true)
    try {
      const payload = new FormData()
      payload.append('vaccine', vaccineName)
      Object.entries(form).forEach(([key, value]) => payload.append(key, value))
      payload.append('photo', photo.file)
      payload.append('paymentScreenshot', paymentScreenshot.file)

      const response = await axios.post('/api/vaccination-applications/', payload)
      setApplicationId(response.data?.id)
      setStep('done')
    } catch (err) {
      const message = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : 'Could not submit your application. Please try again.'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div
        className="container-fluid bg-primary py-5 mb-5 hero-header"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.856), rgba(79, 79, 79, 0.801)), url(${process.env.PUBLIC_URL}/assets/img/hajjpackage.jpg)`,
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <div className="container py-5">
          <div className="row justify-content-center py-5">
            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
              <h1 className="display-4 text-white animated slideInDown">
                {vaccineName} Certificate
              </h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/VisaVaccination" className="text-white">Visa Vaccination</Link>
                  </li>
                  <li className="breadcrumb-item text-white active" aria-current="page">
                    Get Certificate
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xxl py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7">
              <div className="vc-steps mb-5">
                <div className={`vc-step${step === 'details' ? ' active' : ''}${step !== 'details' ? ' done' : ''}`}>
                  <span className="vc-step-num">1</span> Application Details
                </div>
                <div className={`vc-step${step === 'payment' ? ' active' : ''}${step === 'done' ? ' done' : ''}`}>
                  <span className="vc-step-num">2</span> Payment
                </div>
                <div className={`vc-step${step === 'done' ? ' active' : ''}`}>
                  <span className="vc-step-num">3</span> Submitted
                </div>
              </div>

              {step === 'details' && (
                <form className="vc-card" onSubmit={handleDetailsSubmit}>
                  <h4 className="mb-4">Applicant Details</h4>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="As per passport"
                        value={form.fullName}
                        onChange={handleChange('fullName')}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">CNIC Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="42101-1234567-1"
                        value={form.cnic}
                        onChange={handleChange('cnic')}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Passport Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="AB1234567"
                        value={form.passportNumber}
                        onChange={handleChange('passportNumber')}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="+92 300 1234567"
                        value={form.phone}
                        onChange={handleChange('phone')}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="example@mail.com"
                        value={form.email}
                        onChange={handleChange('email')}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Passport-Size Photo</label>
                      {!photo && (
                        <label className="vc-photo-drop">
                          <i className="fa fa-camera me-2" />
                          Click to upload photo (JPG or PNG)
                          <input type="file" accept="image/*" onChange={handlePhotoChange} hidden />
                        </label>
                      )}
                      {photo && (
                        <div className="vc-photo-preview">
                          <img src={photo.previewUrl} alt="Applicant" />
                          <button type="button" className="vc-photo-remove" onClick={removePhoto}>
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary px-4 py-2 mt-4" style={{ borderRadius: '0 30px 30px 0' }}>
                    Continue to Payment <i className="fa fa-arrow-right ms-2" />
                  </button>
                </form>
              )}

              {step === 'payment' && (
                <div className="vc-card">
                  <h4 className="mb-4">Payment</h4>
                  <p className="mb-4">
                    A processing fee of <strong>Rs. {CERTIFICATE_FEE.toLocaleString()}</strong> applies for the{' '}
                    <strong>{vaccineName}</strong> vaccination certificate. Please transfer the fee using the bank
                    details below, then confirm your payment to submit your application.
                  </p>

                  <div className="vc-bank-details mb-4">
                    <div className="vc-bank-row">
                      <span>Bank Name</span>
                      <strong>[Bank Name Placeholder]</strong>
                    </div>
                    <div className="vc-bank-row">
                      <span>Account Title</span>
                      <strong>Falcon Destinations (Pvt.) Ltd</strong>
                    </div>
                    <div className="vc-bank-row">
                      <span>Account Number / IBAN</span>
                      <strong>[Account Number Placeholder]</strong>
                    </div>
                    <div className="vc-bank-row">
                      <span>Amount</span>
                      <strong>Rs. {CERTIFICATE_FEE.toLocaleString()}</strong>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Payment Screenshot</label>
                    {!paymentScreenshot && (
                      <label className="vc-photo-drop">
                        <i className="fa fa-image me-2" />
                        Click to upload your payment/transfer screenshot (JPG or PNG)
                        <input type="file" accept="image/*" onChange={handlePaymentScreenshotChange} hidden />
                      </label>
                    )}
                    {paymentScreenshot && (
                      <div className="vc-photo-preview">
                        <img src={paymentScreenshot.previewUrl} alt="Payment screenshot" />
                        <button type="button" className="vc-photo-remove" onClick={removePaymentScreenshot}>
                          <i className="fa fa-times" />
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                    After transferring the fee and uploading your screenshot, click "I Have Paid — Submit
                    Application" below. Our team will verify the payment and process your certificate within 2
                    business days.
                  </p>

                  <div className="d-flex gap-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary px-4 py-2"
                      style={{ borderRadius: '0 30px 30px 0' }}
                      onClick={() => setStep('details')}
                      disabled={submitting}
                    >
                      <i className="fa fa-arrow-left me-2" /> Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4 py-2"
                      style={{ borderRadius: '0 30px 30px 0' }}
                      onClick={handlePaymentConfirm}
                      disabled={submitting}
                    >
                      <i className="fa fa-check me-2" />
                      {submitting ? 'Submitting…' : 'I Have Paid — Submit Application'}
                    </button>
                  </div>
                </div>
              )}

              {step === 'done' && (
                <div className="vc-card text-center">
                  <div className="vc-success-icon">
                    <i className="fa fa-check" />
                  </div>
                  <h4 className="mb-3">Application Submitted!</h4>
                  {applicationId && (
                    <p className="mb-2">
                      Application Reference: <strong>#{applicationId}</strong>
                    </p>
                  )}
                  <p className="mb-4">
                    Thank you, {form.fullName}. Your <strong>{vaccineName}</strong> certificate application has been
                    received. Our team will review your details and payment, and your certificate will be ready
                    within <strong>2 business days</strong>. We will contact you at {form.email} / {form.phone}.
                  </p>
                  <Link to="/VisaVaccination" className="btn btn-primary px-4 py-2" style={{ borderRadius: '0 30px 30px 0' }}>
                    Back to Visa Vaccination
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VaccinationCertificate
